/**
 * resourceEffects.js — Resource Effect Executors (7 types)
 *
 * Manages hope, stress, and custom token resources.
 * All effects mutate the deep copy passed via context.character.
 *
 * Note: mark_stress as an EFFECT does NOT check maxStress (that's the cost's job).
 * This is used for effects that inflict stress as a consequence, not as a gated payment.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #175)
 */

import { resolveExpression } from '../expressions';

// ─── Resource Effect Executors ──────────────────────────────────────────────

export const RESOURCE_EFFECT_EXECUTORS = {
  /**
   * Gain hope, clamped to maxHope.
   * effect: { type: 'gain_hope', amount? }
   */
  gain_hope: (effect, context) => {
    const amount = resolveExpression(effect.amount || 1, context);
    const maxHope = context.character.maxHope || 6;
    context.character.hope = Math.min(
      (context.character.hope || 0) + amount,
      maxHope
    );
    return { characterMutated: true };
  },

  /**
   * Clear (reduce) stress, floored at 0.
   * effect: { type: 'clear_stress', amount? }
   */
  clear_stress: (effect, context) => {
    const amount = resolveExpression(effect.amount || 1, context);
    context.character.stress = Math.max(
      (context.character.stress || 0) - amount,
      0
    );
    return { characterMutated: true };
  },

  /**
   * Heal (unmark) HP, floored at 0.
   * effect: { type: 'heal_hp', amount? }
   */
  heal_hp: (effect, context) => {
    const amount = resolveExpression(effect.amount || 1, context);
    context.character.hp = Math.max(
      (context.character.hp || 0) - amount,
      0
    );
    return { characterMutated: true };
  },

  /**
   * Mark stress as an effect (consequence, not gated cost).
   * Does NOT validate against maxStress.
   * effect: { type: 'mark_stress', amount? }
   */
  mark_stress: (effect, context) => {
    const amount = resolveExpression(effect.amount || 1, context);
    context.character.stress = (context.character.stress || 0) + amount;
    return { characterMutated: true };
  },

  /**
   * Gain a custom token, clamped to token max.
   * effect: { type: 'gain_token', tokenId, amount? }
   */
  gain_token: (effect, context) => {
    const { tokenId, amount = 1 } = effect;
    const resolvedAmount = resolveExpression(amount, context);
    const token = context.character.cardResources?.[context.cardKey]?.[tokenId];
    if (!token) {
      return { characterMutated: false };
    }
    token.current = Math.min(
      token.current + resolvedAmount,
      token.max !== undefined ? token.max : Infinity
    );
    return { characterMutated: true };
  },

  /**
   * Reset a custom token to 0.
   * effect: { type: 'reset_token', tokenId }
   */
  reset_token: (effect, context) => {
    const token = context.character.cardResources?.[context.cardKey]?.[effect.tokenId];
    if (!token) {
      return { characterMutated: false };
    }
    token.current = 0;
    return { characterMutated: true };
  },

  /**
   * Initialize a token entry if missing, or overwrite with new max/initial.
   * effect: { type: 'initialize_token', tokenId, max, initial? }
   */
  initialize_token: (effect, context) => {
    const { cardKey } = context;
    if (!context.character.cardResources) {
      context.character.cardResources = {};
    }
    if (!context.character.cardResources[cardKey]) {
      context.character.cardResources[cardKey] = {};
    }
    const maxVal = resolveExpression(effect.max, context);
    const initial = effect.initial !== undefined
      ? resolveExpression(effect.initial, context)
      : maxVal;
    context.character.cardResources[cardKey][effect.tokenId] = {
      current: initial,
      max: maxVal,
    };
    return { characterMutated: true };
  },
};
